import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const processCallback = async () => {
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        setError('No session ID received from Google');
        setProcessing(false);
        return;
      }

      try {
        // Send session_id to backend to process Google auth
        const response = await axios.post(`${API}/api/auth/google/session`, {
          session_id: sessionId
        });

        const { access_token, user, is_new_user } = response.data;

        // Store token and user
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(user);

        // Show success message
        if (is_new_user) {
          toast.success('Welcome to Hambre! Your account has been created.');
        } else {
          toast.success('Welcome back!');
        }

        // Redirect based on role
        setTimeout(() => {
          switch (user.role) {
            case 'super_admin':
              navigate('/admin');
              break;
            case 'restaurant_admin':
              navigate('/restaurant');
              break;
            case 'driver':
              navigate('/driver');
              break;
            default:
              navigate('/');
          }
        }, 500);

      } catch (err) {
        console.error('Google auth error:', err);
        const errorMsg = err.response?.data?.detail || 'Failed to sign in with Google';
        setError(errorMsg);
        toast.error(errorMsg);
        setProcessing(false);
      }
    };

    processCallback();
  }, [searchParams, navigate, setUser, setToken]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Sign In Failed</h1>
          <p className="text-slate-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4">
          <svg className="animate-spin w-full h-full text-orange-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Signing you in...</h1>
        <p className="text-slate-500">Please wait while we complete your Google sign in</p>
      </div>
    </div>
  );
}
